import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import GUI from 'lil-gui'
import { vertexColor } from 'three/tsl'

/**
 * Base
 */
// Debug
const gui = new GUI({ width:400 })

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

/**
 * Galaxy
 */
const parameters = {}
parameters.count = 100000
parameters.size = 0.01
parameters.radius = 5
parameters.branches = 3
parameters.spin = 1
parameters.randomness = 0.2
parameters.randomnessPower = 3
parameters.insideColor = "#ff6030"
parameters.outsideColor = "#1b3984"
parameters.particleDensity = 3

let particleGeometry = null
let pointsMaterial = null
let points = null

const generateGalaxy = () => {
    /**
     * Destroy Old Galaxy
     */
    if (points !== null) {
        particleGeometry.dispose()
        pointsMaterial.dispose()
        scene.remove(points)
    }

    /**
     * Geometry
     */
    particleGeometry = new THREE.BufferGeometry()

    const positions = new Float32Array(parameters.count * 3)
    const colors = new Float32Array(parameters.count * 3)

    const colorInside = new THREE.Color(parameters.insideColor)
    const colorOutside = new THREE.Color(parameters.outsideColor)

    for (let i = 0; i < parameters.count; i++) {
        const i3 = i*3

        // Position
        // radius of this particle
        const radius = Math.random() * parameters.radius

        // The greater the particle is away from the center the greater its spin angle
        const spinAngle = radius * parameters.spin

        // angle in radians
        const branchAngle = (i % parameters.branches) / parameters.branches * Math.PI * 2

        // Note - the mutiplication at the end makes the offset go in random directions about 50% of the time
        const randomX = Math.pow(Math.random(), parameters.randomnessPower) * (Math.random() < 0.5 ? 1 : -1) 
        const randomY = Math.pow(Math.random(), parameters.randomnessPower) * (Math.random() < 0.5 ? 1 : -1)
        const randomZ = Math.pow(Math.random(), parameters.randomnessPower) * (Math.random() < 0.5 ? 1 : -1)

        // x
        positions[i3] = Math.cos(branchAngle + spinAngle) * radius + randomX // Gives 2D x coordinate of the circle 
        // y
        positions[i3+1] = randomY
        // z
        positions[i3+2] = Math.sin(branchAngle + spinAngle) * radius + randomZ // Gives 2D y coordinate of the circle

        // Color
        const mixedColor = colorInside.clone()
        mixedColor.lerp(colorOutside, radius / parameters.radius)

        colors[i3] = mixedColor.r
        colors[i3 + 1] = mixedColor.g
        colors[i3 + 2] = mixedColor.b
    }

    particleGeometry.setAttribute(
        "position", new THREE.BufferAttribute(positions, 3)
    )

    particleGeometry.setAttribute(
        "color", new THREE.BufferAttribute(colors, 3)
    )

    /**
     * Material
     */
    pointsMaterial = new THREE.PointsMaterial({
        size: parameters.size,
        sizeAttenuation: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        vertexColors: true
    })
    /**
     * Points
     */
    points = new THREE.Points(particleGeometry, pointsMaterial)
    scene.add(points)

}

generateGalaxy()

gui.add(parameters, "count").min(100).max(1000000).step(100).onFinishChange(generateGalaxy)
gui.add(parameters, "size").min(0.001).max(0.1).step(0.001).onFinishChange(generateGalaxy)
gui.add(parameters, "radius").min(0.01).max(20).step(0.01).onFinishChange(generateGalaxy)
gui.add(parameters, "branches").min(2).max(20).step(1).onFinishChange(generateGalaxy)
gui.add(parameters, "spin").min(-5).max(5).step(0.001).onFinishChange(generateGalaxy)
gui.add(parameters, "randomness").min(0).max(2).step(0.001).onFinishChange(generateGalaxy)
gui.add(parameters, "randomnessPower").min(1).max(10).step(0.001).onFinishChange(generateGalaxy)
gui.addColor(parameters, "insideColor").onFinishChange(generateGalaxy)
gui.addColor(parameters, "outsideColor").onFinishChange(generateGalaxy)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () => {
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
camera.position.x = 3
camera.position.y = 3
camera.position.z = 3
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () => {
    const elapsedTime = clock.getElapsedTime()

    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()
